import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, ButtonBase, Chip, InputAdornment, LinearProgress, Paper, Skeleton, Stack, TextField, Tooltip,
  Typography, alpha, darken, lighten, useTheme,
} from '@mui/material';
import { Add, GradeOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../context/AuthContext';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { useSequentialCreate } from '../../hooks/useSequentialCreate';
import { settingsService } from '../../services/settingsService';
import { Grade } from '../../types';
import { getGradeFromPercentage } from '../../utils/gradeUtils';
import SettingsList, { SettingsTile } from '../common/SettingsList';
import SettingsDialog from '../common/SettingsDialog';
import ColorSwatchPicker, { SWATCHES, swatchAt } from '../common/ColorSwatchPicker';

/** Every grade the school has — a settings list is never paged. */
const ALL = 500;

interface Band { name: string; min: number; max: number; color: string }
type Segment = { from: number; to: number; band?: Band };

const round2 = (n: number) => Math.round(n * 100) / 100;
const pct = (n: number) => `${round2(n)}%`;
const range = (min: number, max: number) => `${pct(min)} – ${pct(max)}`;
const HEX = /^#([0-9a-f]{3}){1,2}$/i;

const toBand = (g: Grade): Band => ({ name: g.name, min: g.minPercentage, max: g.maxPercentage, color: g.color });

/** Narrower than this between two bands is rounding, not a gap: 70–79 then 80–100. */
const ROUNDING_GAP = 1;

/**
 * The 0–100 line split into bands and the gaps between them. Bounds are
 * inclusive to two decimals, so a band 70–79.99 ends where 80 begins. A
 * sliver under ROUNDING_GAP joins the band below, which is the grade those
 * marks get anyway.
 */
const segmentsOf = (bands: Band[]): Segment[] => {
  const out: Segment[] = [];
  let cursor = 0;
  [...bands].sort((a, b) => a.min - b.min).forEach(band => {
    const prev = out[out.length - 1];
    if (band.min > cursor && prev?.band && band.min - cursor < ROUNDING_GAP) prev.to = band.min;
    else if (band.min > cursor) out.push({ from: cursor, to: band.min });
    const end = Math.min(100, round2(band.max + 0.01));
    const start = Math.max(band.min, cursor);
    if (end > start) out.push({ from: start, to: end, band });
    cursor = Math.max(cursor, end);
  });
  const last = out[out.length - 1];
  if (cursor < 100 && last?.band && 100 - cursor < ROUNDING_GAP) last.to = 100;
  else if (cursor < 100) out.push({ from: cursor, to: 100 });
  return out;
};

/** Uncovered ranges as inclusive min/max, ready to use as a grade's bounds. */
const gapsOf = (bands: Band[]) =>
  segmentsOf(bands)
    .filter(s => !s.band)
    .map(s => ({ min: s.from, max: s.to === 100 ? 100 : round2(s.to - 0.01) }));

const PRESETS: Array<{ label: string; description: string; bands: Band[] }> = [
  {
    label: 'A1 to F',
    description: 'Seven grades, ten points apart',
    bands: [
      { name: 'A1', min: 90, max: 100, color: SWATCHES[0] },
      { name: 'A', min: 80, max: 89.99, color: SWATCHES[5] },
      { name: 'B', min: 70, max: 79.99, color: SWATCHES[1] },
      { name: 'C', min: 60, max: 69.99, color: SWATCHES[8] },
      { name: 'D', min: 50, max: 59.99, color: SWATCHES[6] },
      { name: 'E', min: 40, max: 49.99, color: SWATCHES[2] },
      { name: 'F', min: 0, max: 39.99, color: SWATCHES[3] },
    ],
  },
  {
    label: 'A to F',
    description: 'Five grades, pass from 50%',
    bands: [
      { name: 'A', min: 80, max: 100, color: SWATCHES[0] },
      { name: 'B', min: 70, max: 79.99, color: SWATCHES[1] },
      { name: 'C', min: 60, max: 69.99, color: SWATCHES[6] },
      { name: 'D', min: 50, max: 59.99, color: SWATCHES[2] },
      { name: 'F', min: 0, max: 49.99, color: SWATCHES[3] },
    ],
  },
];

/** The whole scale as one bar: each band in its colour, gaps hatched. */
const ScaleBar: React.FC<{ bands: Band[]; compact?: boolean; describeGap?: (min: number) => string }> = ({
  bands, compact = false, describeGap,
}) => {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const ink = (c: string) => (dark ? lighten(c, 0.35) : darken(c, 0.35));
  const hatch = alpha(theme.palette.text.primary, dark ? 0.12 : 0.08);
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: '2px', height: compact ? 18 : 40, borderRadius: compact ? '5px' : '10px', overflow: 'hidden' }}>
        {segmentsOf(bands).map(s => {
          const width = s.to - s.from;
          const max = s.to === 100 ? 100 : round2(s.to - 0.01);
          const title = s.band
            ? `${s.band.name}: ${range(s.band.min, s.band.max)}`
            : `Not covered: ${range(s.from, max)}${describeGap ? `. ${describeGap(s.from)}` : ''}`;
          return (
            <Tooltip key={`${s.from}-${s.band?.name ?? 'gap'}`} title={title} disableInteractive>
              <Box
                sx={{
                  flex: `${width} 1 0`,
                  minWidth: s.band ? 2 : 6,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  ...(s.band
                    ? {
                        bgcolor: alpha(s.band.color, dark ? 0.32 : 0.2),
                        boxShadow: `inset 0 -3px 0 ${s.band.color}`,
                        color: ink(s.band.color),
                      }
                    : {
                        backgroundImage: `repeating-linear-gradient(135deg, ${hatch} 0 4px, transparent 4px 8px)`,
                        boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.text.primary, 0.08)}`,
                      }),
                }}
              >
                {s.band && !compact && width >= 5 && (
                  <Typography noWrap sx={{ fontSize: '0.75rem', fontWeight: 700, px: 0.5 }}>{s.band.name}</Typography>
                )}
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      {!compact && (
        <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
          {[0, 50, 100].map(t => (
            <Typography key={t} variant="caption" color="text.disabled" sx={{ fontVariantNumeric: 'tabular-nums' }}>{t}%</Typography>
          ))}
        </Stack>
      )}
    </Box>
  );
};

const emptyForm = { name: '', min: '', max: '', color: SWATCHES[0] };

const GradesSettings: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const { canRead, canWrite } = useModulePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const writable = canWrite('settings_grades');
  const bulk = useSequentialCreate();

  const [grades, setGrades] = useState<Grade[]>([]);
  const [loading, setLoading] = useState(true);
  /** The grade being edited, `'new'` when adding, or null when closed. */
  const [editing, setEditing] = useState<Grade | 'new' | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [preset, setPreset] = useState<(typeof PRESETS)[number] | null>(null);
  /** The failing grade from Settings → Results, marked on its row. */
  const [failingGradeId, setFailingGradeId] = useState<string | null>(null);
  const showFailing = canRead('settings_results');

  useEffect(() => {
    if (!showFailing) return;
    settingsService.getResultSettings()
      .then((res) => setFailingGradeId(res.data?.failingGradeId ?? null))
      .catch((e: any) => enqueueSnackbar(e?.response?.data?.message || 'Could not load the failing grade', { variant: 'error' }));
  }, [showFailing, enqueueSnackbar]);

  const load = useCallback(async () => {
    try {
      const res = await settingsService.getGrades({ isActive: true, limit: ALL });
      setGrades(res.data?.grades ?? []);
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Could not load grades', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const sorted = useMemo(() => [...grades].sort((a, b) => b.minPercentage - a.minPercentage), [grades]);
  const bands = useMemo(() => sorted.map(toBand), [sorted]);
  const gaps = useMemo(() => gapsOf(bands), [bands]);
  const largestGap = useMemo(
    () => gaps.reduce<(typeof gaps)[number] | null>((best, g) => (!best || g.max - g.min > best.max - best.min ? g : best), null),
    [gaps],
  );
  // A gap is not ungraded: reports give its marks whatever grade the scale falls back to.
  const gradeAt = (mark: number) => getGradeFromPercentage(mark, grades);
  const describeGap = (min: number) => `Marks here are given ${gradeAt(min)}.`;

  const openNew = () => {
    if (!largestGap) return;
    setForm({
      name: '',
      min: String(largestGap.min),
      max: String(largestGap.max),
      color: SWATCHES.find(c => !grades.some(g => g.color.toLowerCase() === c)) ?? swatchAt(grades.length),
    });
    setEditing('new');
  };

  const edit = (g: Grade) => {
    setForm({ name: g.name, min: String(g.minPercentage), max: String(g.maxPercentage), color: g.color });
    setEditing(g);
  };

  // Live checks, mirroring the server's: bounds 0–100, max ≥ min, inclusive overlap.
  const editingId = editing && editing !== 'new' ? editing._id : null;
  const others = sorted.filter(g => g._id !== editingId);
  const name = form.name.trim();
  const min = form.min === '' ? NaN : Number(form.min);
  const max = form.max === '' ? NaN : Number(form.max);
  const boundsOk = Number.isFinite(min) && Number.isFinite(max) && min >= 0 && max <= 100 && min <= max;
  const overlaps = boundsOk ? others.filter(g => g.minPercentage <= max && min <= g.maxPercentage) : [];
  const freeRanges = gapsOf(others.map(toBand));
  const duplicate = others.find(g => g.name.trim().toLowerCase() === name.toLowerCase());
  const minError = form.min !== '' && (!Number.isFinite(min) || min < 0 || min > 100) ? 'Between 0 and 100' : '';
  const maxError = form.max !== '' && (!Number.isFinite(max) || max < 0 || max > 100)
    ? 'Between 0 and 100'
    : Number.isFinite(min) && Number.isFinite(max) && max < min ? 'At least the minimum' : '';
  const invalid = !name || !!duplicate || !boundsOk || overlaps.length > 0 || !HEX.test(form.color);

  const save = async () => {
    if (!editing || invalid) return;
    const data = { name, minPercentage: round2(min), maxPercentage: round2(max), color: form.color };
    setSaving(true);
    try {
      if (editing === 'new') {
        await settingsService.createGrade({ ...data, schoolId: user?.schoolId?._id });
        enqueueSnackbar(`Grade ${name} added`, { variant: 'success' });
      } else {
        await settingsService.updateGrade(editing._id, data);
        enqueueSnackbar('Grade saved', { variant: 'success' });
      }
      setEditing(null);
      load();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Could not save this grade', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const applyPreset = async () => {
    if (!preset) return;
    const { added, failed } = await bulk.run(preset.bands, b => settingsService.createGrade({
      name: b.name, minPercentage: b.min, maxPercentage: b.max, color: b.color, schoolId: user?.schoolId?._id,
    }));
    if (added > 0) enqueueSnackbar(`${added} grade${added === 1 ? '' : 's'} added`, { variant: 'success' });
    if (failed.length) {
      enqueueSnackbar(
        `Not added: ${failed.map(f => `${f.item.name} (${f.message})`).join(', ')}`,
        { variant: 'error', autoHideDuration: 10000 },
      );
    }
    setPreset(null);
    load();
  };

  const removalConsequence = (g: Grade) => {
    const remaining = grades.filter(o => o._id !== g._id);
    const low = getGradeFromPercentage(g.minPercentage, remaining);
    const high = getGradeFromPercentage(g.maxPercentage, remaining);
    const given = remaining.length === 0
      ? 'graded by the built-in fallback scale'
      : `given ${low === high ? low : `${low} to ${high}`}`;
    return `Grades are worked out from the scale each time, so marks of ${range(g.minPercentage, g.maxPercentage)} will be ${given} from now on. Results already saved with ${g.name} keep it.`;
  };

  return (
    <Stack spacing={2}>
      {loading ? (
        <Skeleton variant="rounded" height={88} sx={{ borderRadius: '12px' }} />
      ) : grades.length > 0 ? (
        <Paper variant="outlined" sx={{ borderRadius: '12px', p: 2 }}>
          <ScaleBar bands={bands} describeGap={describeGap} />
          <Typography variant="caption" color={gaps.length ? 'warning.main' : 'text.secondary'} sx={{ display: 'block', mt: 1 }}>
            {gaps.length
              ? `Not covered: ${gaps.map(g => `${range(g.min, g.max)} (given ${gradeAt(g.min)})`).join(', ')}.`
              : `${grades.length} grade${grades.length === 1 ? '' : 's'} covering every mark from 0% to 100%.`}
          </Typography>
        </Paper>
      ) : writable && (
        <Paper variant="outlined" sx={{ borderRadius: '12px', p: 2 }}>
          <Typography variant="subtitle2" fontWeight={700}>Start with a standard scale</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Adds every grade in one go. You can rename, recolour or adjust them afterwards.
          </Typography>
          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            {PRESETS.map(p => (
              <ButtonBase
                key={p.label}
                onClick={() => setPreset(p)}
                sx={{
                  display: 'block',
                  textAlign: 'left',
                  p: 1.5,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: '10px',
                  transition: theme.transitions.create(['border-color', 'background-color'], { duration: 120 }),
                  '&:hover': { borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.04) },
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>{p.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{p.description}</Typography>
                </Stack>
                <ScaleBar bands={p.bands} compact />
              </ButtonBase>
            ))}
          </Box>
        </Paper>
      )}

      <SettingsList
        rows={sorted}
        loading={loading}
        rowKey={g => g._id}
        name={g => g.name}
        subtitle={g => `${range(g.minPercentage, g.maxPercentage)}${g._id === failingGradeId ? ' · failing grade (Settings → Results)' : ''}`}
        leading={g => <SettingsTile color={g.color}>{g.name.slice(0, 3)}</SettingsTile>}
        searchText={g => g.name}
        canWrite={writable}
        onEdit={edit}
        remove={{ run: g => settingsService.deleteGrade(g._id), noun: 'grade', consequence: removalConsequence }}
        reload={load}
        toolbar={writable && grades.length > 0 ? (
          <Tooltip title={largestGap ? '' : 'The scale already covers 0% to 100%. Narrow a grade to make room.'}>
            <span>
              <Button variant="contained" disableElevation size="small" startIcon={<Add />} onClick={openNew} disabled={!largestGap}>
                Add grade
              </Button>
            </span>
          </Tooltip>
        ) : undefined}
        empty={{
          icon: <GradeOutlined />,
          title: 'No grades yet',
          description: writable
            ? 'Pick a standard scale above to add every grade at once.'
            : 'Grades appear here once someone with access sets up the scale.',
        }}
      />

      <SettingsDialog
        open={!!editing}
        title={editing === 'new' ? 'Add grade' : 'Edit grade'}
        subtitle={editing === 'new' ? 'Pre-filled with the largest range the scale does not cover yet.' : undefined}
        onClose={() => setEditing(null)}
        onSave={save}
        saving={saving}
        saveLabel={editing === 'new' ? 'Add grade' : 'Save'}
        saveDisabled={invalid}
      >
        <TextField
          label="Name"
          value={form.name}
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          placeholder="e.g. A1, A, B"
          inputProps={{ maxLength: 10 }}
          error={!!duplicate}
          helperText={duplicate ? `Another grade is already called ${duplicate.name}` : 'Up to 10 characters'}
          fullWidth
        />
        <Stack direction="row" spacing={2}>
          {([['min', 'From', minError], ['max', 'To', maxError]] as const).map(([key, label, error]) => (
            <TextField
              key={key}
              label={label}
              type="number"
              value={form[key]}
              onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
              error={!!error}
              helperText={error || ' '}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              fullWidth
            />
          ))}
        </Stack>
        {overlaps.length > 0 && (
          <Alert severity="warning" sx={{ mt: '8px !important' }}>
            Overlaps {overlaps.map(g => `${g.name} (${range(g.minPercentage, g.maxPercentage)})`).join(', ')}.
            Bounds are inclusive, so a band that ends at 80% and one that starts at 80% overlap.
            {freeRanges.length > 0 ? (
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.75 }}>
                <Typography variant="caption">Free:</Typography>
                {freeRanges.map(r => (
                  <Chip
                    key={r.min}
                    size="small"
                    label={range(r.min, r.max)}
                    onClick={() => setForm(f => ({ ...f, min: String(r.min), max: String(r.max) }))}
                    sx={{ borderRadius: '7px', fontWeight: 500 }}
                  />
                ))}
              </Stack>
            ) : (
              <Box sx={{ mt: 0.5 }}>No range is free. Narrow another grade first.</Box>
            )}
          </Alert>
        )}
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Colour</Typography>
          <ColorSwatchPicker value={form.color} onChange={color => setForm(f => ({ ...f, color }))} custom />
        </Box>
      </SettingsDialog>

      <SettingsDialog
        open={!!preset}
        title={preset ? `Use the ${preset.label} scale` : ''}
        subtitle={preset ? `Adds ${preset.bands.length} grades. You can adjust any of them afterwards.` : undefined}
        onClose={() => setPreset(null)}
        onSave={applyPreset}
        saving={bulk.busy}
        saveLabel={preset ? `Add ${preset.bands.length} grades` : 'Add'}
      >
        {preset && (
          <>
            <ScaleBar bands={preset.bands} />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 3, rowGap: 1 }}>
              {preset.bands.map(b => (
                <Stack key={b.name} direction="row" spacing={1.25} alignItems="center">
                  <SettingsTile color={b.color}>{b.name}</SettingsTile>
                  <Typography variant="body2" color="text.secondary">{range(b.min, b.max)}</Typography>
                </Stack>
              ))}
            </Box>
            {bulk.progress && (
              <Box>
                <LinearProgress variant="determinate" value={(bulk.progress.done / bulk.progress.total) * 100} sx={{ borderRadius: 2 }} />
                <Typography variant="caption" color="text.secondary">
                  Adding {bulk.progress.done + 1} of {bulk.progress.total}
                </Typography>
              </Box>
            )}
          </>
        )}
      </SettingsDialog>
    </Stack>
  );
};

export default GradesSettings;
